import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Select from "react-select";
import { getCrmApiBaseUrl } from "../../utils/utils";
import "../../wallet.css";
import { Pill } from "./walletShared";

/**
 * In-scan Service-Time cutoff config (driver frontend).
 *
 * An order is classified at warehouse in-scan by comparing its in-scan time-of-day to a cutoff:
 * strictly AFTER -> "Same Day"; at/before -> "Next Day". This page edits the single global default
 * plus optional per-partner overrides. CRUD: {REACT_APP_API_URL}shypvdriverapp/inscan-cutoff
 */
const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderColor: state.isFocused ? "#00a6ed" : "#e7eaf0",
    boxShadow: "none",
    "&:hover": { borderColor: "#00a6ed" },
  }),
};

export default function InScanCutoffConfig() {
  const API_URL = process.env.REACT_APP_API_URL; // driver API, trailing slash
  const API = `${API_URL}shypvdriverapp/inscan-cutoff`;

  const [rows, setRows] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  // add-override form
  const [newPartner, setNewPartner] = useState(null);
  const [newCutoff, setNewCutoff] = useState("09:00");

  const notify = (type, text) => {
    setBanner({ type, text });
    setTimeout(() => setBanner(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      setRows((res.data && res.data.data) || []);
    } catch (err) {
      notify("danger", "Could not load cutoff config: " + (err?.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [API]);

  const fetchPartners = useCallback(async () => {
    try {
      const base = getCrmApiBaseUrl();
      const res = await axios.get(`${base}/v1.0/partner/retrieve_list?page=0`);
      setPartners((res.data && res.data.data) || []);
    } catch (err) {
      console.warn("Could not load partners:", err.message);
    }
  }, []);

  useEffect(() => {
    load();
    fetchPartners();
  }, [load, fetchPartners]);

  const keyOf = (row) => (row.partnerId == null ? "global" : `p${row.partnerId}`);
  const isGlobal = (row) => row.partnerId == null;

  const patchRow = (key, field, value) =>
    setRows((prev) => prev.map((r) => (keyOf(r) === key ? { ...r, [field]: value } : r)));

  const saveRow = async (row) => {
    const key = keyOf(row);
    setSavingKey(key);
    try {
      const body = {
        partnerId: row.partnerId ?? null,
        partnerName: row.partnerName ?? null,
        cutoffTime: row.cutoffTime,
        timezone: row.timezone || "Asia/Dubai",
        enabled: isGlobal(row) ? true : row.enabled !== false,
      };
      if (row.id) await axios.put(`${API}/${row.id}`, body);
      else await axios.post(API, body);
      notify("success", "Cutoff saved");
      load();
    } catch (err) {
      notify("danger", "Save failed: " + (err?.response?.data?.message || err.message));
    } finally {
      setSavingKey(null);
    }
  };

  const deleteRow = async (row) => {
    if (isGlobal(row) || !row.id) return;
    if (!window.confirm(`Remove the cutoff override for ${row.partnerName || "#" + row.partnerId}?`)) return;
    try {
      await axios.delete(`${API}/${row.id}`);
      notify("success", "Override removed");
      load();
    } catch (err) {
      notify("danger", "Delete failed: " + (err?.response?.data?.message || err.message));
    }
  };

  const addOverride = async () => {
    if (!newPartner) {
      notify("danger", "Pick a partner first");
      return;
    }
    setSavingKey("new");
    try {
      await axios.post(API, {
        partnerId: newPartner.value,
        partnerName: newPartner.pname,
        cutoffTime: newCutoff,
        timezone: "Asia/Dubai",
        enabled: true,
      });
      notify("success", "Override added");
      setNewPartner(null);
      setNewCutoff("09:00");
      load();
    } catch (err) {
      notify("danger", "Could not add override: " + (err?.response?.data?.message || err.message));
    } finally {
      setSavingKey(null);
    }
  };

  const partnerOptions = partners
    .slice()
    .sort((a, b) => (a.pname || "").localeCompare(b.pname || ""))
    .map((p) => ({ value: p.pid, label: `${String(p.pid).padStart(2, "0")} - ${p.pname}`, pname: p.pname }));

  const globalRow = rows.find(isGlobal);
  const overrideRows = rows.filter((r) => !isGlobal(r));

  return (
    <div className="wallet-page">
      <div className="w-topbar">
        <div className="w-topbar-left">
          <div className="w-icon-badge"><i className="fa fa-clock-o" aria-hidden="true" /></div>
          <div>
            <h2 className="w-title">In-scan Service-Time Cutoff</h2>
            <p className="w-subtitle">
              Orders in-scanned <strong>after</strong> the cutoff are <strong>Same Day</strong>; at/before are <strong>Next Day</strong>.
              The global default applies to every partner without an override.
            </p>
          </div>
        </div>
      </div>

      {banner && (
        <div className={`w-pill w-pill-${banner.type}`} style={{ marginBottom: 14 }}>{banner.text}</div>
      )}

      {/* Global default */}
      <div className="w-card">
        <div className="w-card-header">
          <span className="w-card-title"><i className="fa fa-globe" aria-hidden="true" /> Global default</span>
        </div>
        <div className="w-card-body">
          {loading && !globalRow ? (
            <div className="w-loading"><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Loading…</div>
          ) : globalRow ? (
            <div className="w-form-row" style={{ alignItems: "flex-end" }}>
              <div className="w-field" style={{ flex: "0 0 180px" }}>
                <label className="w-field-label">Cutoff time</label>
                <input
                  type="time"
                  className="w-input"
                  value={globalRow.cutoffTime || "09:00"}
                  onChange={(e) => patchRow("global", "cutoffTime", e.target.value)}
                />
              </div>
              <div className="w-field" style={{ flex: "0 0 180px" }}>
                <label className="w-field-label">Timezone</label>
                <input
                  type="text"
                  className="w-input"
                  value={globalRow.timezone || "Asia/Dubai"}
                  onChange={(e) => patchRow("global", "timezone", e.target.value)}
                />
              </div>
              <div className="w-field" style={{ flex: "0 0 auto" }}>
                <button
                  type="button"
                  className="w-btn w-btn-primary"
                  disabled={savingKey === "global"}
                  onClick={() => saveRow(globalRow)}
                >
                  {savingKey === "global" ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <div className="w-help-text">No global row found. Saving here will create it.</div>
          )}
        </div>
      </div>

      {/* Per-partner overrides */}
      <div className="w-card">
        <div className="w-card-header">
          <span className="w-card-title"><i className="fa fa-users" aria-hidden="true" /> Per-partner overrides</span>
          {overrideRows.length > 0 && <span className="w-card-meta">{overrideRows.length} configured</span>}
        </div>
        <div className="w-card-body">
          {/* add form */}
          <div className="w-form-row" style={{ alignItems: "flex-end", marginBottom: overrideRows.length ? 18 : 0 }}>
            <div className="w-field" style={{ flex: "1 1 280px" }}>
              <label className="w-field-label">Partner</label>
              <Select
                styles={selectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                options={partnerOptions}
                value={newPartner}
                onChange={setNewPartner}
                placeholder="Select a partner…"
                isClearable
              />
            </div>
            <div className="w-field" style={{ flex: "0 0 160px" }}>
              <label className="w-field-label">Cutoff time</label>
              <input type="time" className="w-input" value={newCutoff} onChange={(e) => setNewCutoff(e.target.value)} />
            </div>
            <div className="w-field" style={{ flex: "0 0 auto" }}>
              <button type="button" className="w-btn w-btn-primary" disabled={savingKey === "new"} onClick={addOverride}>
                {savingKey === "new" ? "Adding…" : "Add override"}
              </button>
            </div>
          </div>

          {overrideRows.length > 0 && (
            <div className="w-scan-list" style={{ maxHeight: "none" }}>
              {overrideRows.map((row) => {
                const key = keyOf(row);
                return (
                  <div key={key} className="w-scan-row" style={{ flexWrap: "wrap", gap: 12 }}>
                    <span className="w-scan-awb" style={{ minWidth: 160 }}>
                      {row.partnerName || `#${row.partnerId}`}
                    </span>
                    <input
                      type="time"
                      className="w-input"
                      style={{ width: 130, flex: "0 0 auto" }}
                      value={row.cutoffTime || "09:00"}
                      onChange={(e) => patchRow(key, "cutoffTime", e.target.value)}
                    />
                    <label className="w-toggle-row" style={{ margin: 0, flex: "0 0 auto" }}>
                      <span className="w-toggle">
                        <input
                          type="checkbox"
                          checked={row.enabled !== false}
                          onChange={(e) => patchRow(key, "enabled", e.target.checked)}
                        />
                        <span className="w-toggle-slider" />
                      </span>
                      <span className="w-toggle-label">{row.enabled !== false ? "Enabled" : "Disabled"}</span>
                    </label>
                    {row.enabled === false && <Pill tone="neutral">falls back to global</Pill>}
                    <button
                      type="button"
                      className="w-btn w-btn-primary"
                      style={{ flex: "0 0 auto" }}
                      disabled={savingKey === key}
                      onClick={() => saveRow(row)}
                    >
                      {savingKey === key ? "Saving…" : "Save"}
                    </button>
                    <button
                      type="button"
                      className="w-scan-remove"
                      style={{ flex: "0 0 auto" }}
                      onClick={() => deleteRow(row)}
                      aria-label="Remove override"
                    >
                      <i className="fa fa-trash" aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
